import { createFeatureSelector, createSelector } from '@ngrx/store';
import { List, Map, Set } from 'immutable';

import { Loadable } from '®shared/data';
import {
  Container,
  EditableList,
  EditableSection,
  PageableItems,
  ReadingListDetails,
} from '®shared/reading-list/models';

import {
  containerKey,
  getAllKeysFromContainerStructure,
  getContainedTypeAndIdOnly,
  getPathReadoutsByLeaf,
} from './container-structure-utils';
import { CreateOrUpdate } from './create-or-update';

export const STATE_FEATURE_CONTAINER = 'container';

export interface ContainedTypeAndId {
  type: 'items' | 'lists' | 'sections';
  id: string;

  equals(other: any): boolean;
  hashCode(): number;
}

export type ContainerData = {
  rootKey: string;
  allKeys: Set<ContainedTypeAndId>;
  pathReadouts: Map<string, List<string>>;
};

export type ListOrSectionContainedTypeAndId = ContainedTypeAndId & {
  type: 'lists' | 'sections';
};

export type ItemOrSectionContainedTypeAndId = ContainedTypeAndId & {
  type: 'items' | 'sections';
};

export type ContainedTypeAndIdTypes =
  | ContainedTypeAndId
  | ItemOrSectionContainedTypeAndId
  | ListOrSectionContainedTypeAndId;

export type ContainerStructure = Map<string, List<ContainedTypeAndId>>;

export interface ContainerState {
  container: Container;
  containerStructure: ContainerStructure;
  noteItemsBeingEdited: Map<string, CreateOrUpdate>;
  studentNotesBeingEdited: Map<string, CreateOrUpdate>;
  sectionsBeingEdited: Map<string, CreateOrUpdate>;
  sectionsById: Map<string, EditableSection>;
  libraryNotesBeingEdited: Map<string, CreateOrUpdate>;
  listsById: Map<string, EditableList>;
  loadableItemPagesByOffset: Map<number, Loadable<PageableItems>>;
}

export const CONTAINER_INITIAL_STATE: ContainerState = {
  container: null,
  containerStructure: Map<string, List<ContainedTypeAndId>>(),
  noteItemsBeingEdited: Map<string, CreateOrUpdate>(),
  studentNotesBeingEdited: Map<string, CreateOrUpdate>(),
  sectionsBeingEdited: Map<string, CreateOrUpdate>(),
  sectionsById: Map<string, EditableSection>(),
  libraryNotesBeingEdited: Map<string, CreateOrUpdate>(),
  listsById: Map<string, EditableList>(),
  loadableItemPagesByOffset: Map<number, Loadable<PageableItems>>(),
};

export function getInitialContainerState(): ContainerState {
  return CONTAINER_INITIAL_STATE;
}

export const selectContainerState = createFeatureSelector<ContainerState>(
  STATE_FEATURE_CONTAINER,
);

export const selectContainer = createSelector(
  selectContainerState,
  (state: ContainerState): Container | null => state.container,
);

export const selectContainerStructure = createSelector(
  selectContainerState,
  (state: ContainerState) => state.containerStructure,
);

export const selectContainerData = createSelector(
  selectContainer,
  selectContainerStructure,
  (container, containerStructure): ContainerData | null => {
    if (!container || !containerStructure || containerStructure.isEmpty()) {
      return null;
    }

    return getContainerData(containerStructure, container);
  },
);

export const selectContainerPathReadouts = createSelector(
  selectContainerData,
  (containerData: ContainerData | null) =>
    containerData && containerData.pathReadouts,
);

export const selectSectionsBeingEditedState = createSelector(
  selectContainerState,
  (state: ContainerState) => state.sectionsBeingEdited,
);

export const selectNoteItemsBeingEditedState = createSelector(
  selectContainerState,
  (state: ContainerState) => state.noteItemsBeingEdited,
);

export const selectSectionsById = createSelector(
  selectContainerState,
  (state: ContainerState) => state.sectionsById,
);

export const selectListsById = createSelector(
  selectContainerState,
  (state: ContainerState) => state.listsById,
);

export const selectStudentNotesBeingEditedState = createSelector(
  selectContainerState,
  (state: ContainerState) => state.studentNotesBeingEdited,
);

export const selectLibraryNotesBeingEditedState = createSelector(
  selectContainerState,
  (state: ContainerState) => state.libraryNotesBeingEdited,
);

export const selectReadingListDetails = createSelector(
  selectContainer,
  (container: Container | null): ReadingListDetails | null => {
    if (!container) {
      return null;
    } else if (container instanceof ReadingListDetails) {
      return container;
    }

    throw new Error(
      'Attempted to select ReadingListDetails for a non-list container',
    );
  },
);

export function getContainerData(
  containerStructure: Map<string, List<ContainedTypeAndId>>,
  container: Container,
): ContainerData {
  const typeAndId = getContainedTypeAndIdOnly(container);
  const rootKey = containerKey(container);

  const allKeys = getAllKeysFromContainerStructure(
    typeAndId,
    containerStructure,
  );

  const pathReadouts = getPathReadoutsByLeaf(typeAndId, containerStructure);

  return { rootKey, allKeys, pathReadouts };
}
